export type RRuleFrequency = "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY";
export type RRuleByDay = "MO" | "TU" | "WE" | "TH" | "FR" | "SA" | "SU";

export type RRule = {
  freq: RRuleFrequency;
  interval?: number;
  bysetpos?: number;
  byday?: RRuleByDay[];
  bymonthday?: number;
};
const RRULE_HEADER = "RRULE:";

export function getByDayLabel(day: RRuleByDay): string {
  switch (day) {
    case "SU":
      return "Sunday";
    case "MO":
      return "Monday";
    case "TU":
      return "Tuesday";
    case "WE":
      return "Wednesday";
    case "TH":
      return "Thurdsay";
    case "FR":
      return "Friday";
    case "SA":
      return "Saturday";
  }
}

export function getPositionLabel(position: number): string {
  if (!position) {
    return "";
  }

  switch (position) {
    case 1:
      return "First";
    case 2:
      return "Second";
    case 3:
      return "Third";
    case 4:
      return "Fourth";
    case -1:
      return "Last";
  }

  return position.toString();
}

export function rruleToHumanizedString(rrule: string): string {
  const rule = parseRRule(rrule);

  let parts: string[] = ["Occurs every"];

  if (rule.interval && rule.interval > 1) {
    parts.push(rule.interval.toString());
  }

  switch (rule.freq) {
    case "DAILY":
      parts.push(pluralizeIt("day", rule.interval));
      break;
    case "WEEKLY":
      parts.push(pluralizeIt("week", rule.interval));
      break;
    case "MONTHLY":
      parts.push(pluralizeIt("month", rule.interval));
      break;
  }

  if (rule.bymonthday) {
    parts.push(`on the ${rule.bymonthday}${getNth(rule.bymonthday)}`);
  } else if (!rule.bysetpos && rule.byday !== undefined) {
    parts.push(`on ${rule.byday.map(d => getByDayLabel(d).substr(0, 3)).join(", ")}`);
  } else if (rule.bysetpos !== undefined && rule.byday) {
    parts.push(
      `the ${getPositionLabel(rule.bysetpos).toLocaleLowerCase()} ${getByDayLabel(rule.byday[0])}`
    );
  }

  return parts.join(" ");
}

function getNth(day: number): string {
  if (day > 3 && day < 21) return "th";

  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

function pluralizeIt(value: string, quantity: number | undefined) {
  if (!quantity || quantity <= 1) {
    return value;
  } else return `${value}s`;
}

export function parseRRule(rrule: string): RRule {
  const map = parseRRuleToMap(rrule);

  const rule: RRule = {
    freq: map.get("FREQ") as RRuleFrequency,
  };

  if (map.has("INTERVAL")) {
    rule.interval = Number.parseInt(map.get("INTERVAL") as string);
  }

  if (map.has("BYSETPOS")) {
    rule.bysetpos = Number.parseInt(map.get("BYSETPOS") as string);
  }

  if (map.has("BYDAY")) {
    rule.byday = map.get("BYDAY")?.split(",") as RRuleByDay[];
  }

  if (map.has("BYMONTHDAY")) {
    rule.bymonthday = Number.parseInt(map.get("BYMONTHDAY") as string);
  }

  return rule;
}

export function parseRRuleToMap(rrule: string): Map<string, string> {
  const headerPart = rrule.indexOf(RRULE_HEADER);
  let value = rrule;
  if (headerPart === 0) {
    value = rrule.substring(RRULE_HEADER.length);
  }

  const pairs = value.split(";");
  return pairs.reduce((out, pair) => {
    const [key, val] = pair.split("=");
    out.set(key.toUpperCase(), val);
    return out;
  }, new Map<string, string>());
}

export function rruleString(rrule: RRule): string {
  let values: string[] = [];
  Object.keys(rrule).forEach(key => {
    const value = rrule[key as keyof RRule] as unknown;
    if (value) {
      values.push(`${key.toUpperCase()}=${value}`);
    }
  });
  return `${RRULE_HEADER}${values.join(";")}`;
}
